import {
  Box,
  Typography,
  Grid,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
} from '@mui/material';
import TableChartIcon from '@mui/icons-material/TableChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import React, { useState, useEffect } from 'react';
import { PieChart, MixedChart } from './components';
import DashboardSummaryTable from './components/DashboardSummaryTable';
import { reportingApi } from 'src/api/reporting';
import SprintTrendChart from './components/SprintTrendChart';
import logger from 'src/utils/logger';

interface SprintData {
  details: {
    billableAccount: string;
    totalLoggedHoursInSeconds: number;
    totalTimeEstimate: number;
  }[];
  sprintName: string;
}

interface AggregatedData {
  accountName: string;
  totalHoursUtilized: number;
  totalHoursAllocated: number;
}

interface Totals {
  [key: string]: number;
}

// Add interface for billing account
interface BillingAccount {
  id: string;
  name: string;
  key: string;
  accountType: string;
  isBillable: boolean;
  isActive: boolean;
  // ... add other properties if needed
}

// Add interface for mixed chart data
interface MixedChartData {
  bars: {
    name: string;
    data: number[];
  }[];
  lines: {
    name: string;
    data: number[];
  }[];
  categories: string[];
}

// Update the interface for the API response
interface MixChartDataPoint {
  totalTimeEstimate: number;
  totalLoggedHoursInSeconds: number;
  loggedDate: string;
  week: string;
}

interface ApiResponse {
  details: {
    billableAccount: string;
    totalLoggedHoursInSeconds: number;
    totalTimeEstimate: number;
  }[];
  sprintName: string;
}

const DashboardV2 = () => {
  const [showTable, setShowTable] = useState(false);
  const [data, setData] = useState<AggregatedData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSprint, setSelectedSprint] = useState<number>(0);
  const [selectedOrganization, setSelectedOrganization] = useState<string>('');
  const [organizations, setOrganizations] = useState<BillingAccount[]>([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await reportingApi.getPieChartData({
          sprintIndex: selectedSprint.toString(),
        });
        const rawData: any[] = response.data;

        // Aggregate data by billableAccount
        const aggregatedData = rawData.reduce((acc, curr) => {
          const existing = acc.find(
            (item) => item.accountName === curr.accountName
          );
          if (existing) {
            existing.totalHoursUtilized += curr.totalHoursUtilized;
            existing.totalHoursAllocated += curr.totalHoursAllocated;
          } else {
            acc.push({
              accountName: curr.accountName,
              totalHoursUtilized: curr.totalHoursUtilized,
              totalHoursAllocated: curr.totalHoursAllocated,
            });
          }
          return acc;
        }, [] as AggregatedData[]);

        setData(aggregatedData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedSprint]);

  // Fetch billing accounts
  useEffect(() => {
    const fetchBillingAccounts = async () => {
      setLoadingOrganizations(true);
      try {
        const response = await reportingApi.getOrganizations();
        if (response?.data) {
          setOrganizations(response.data);
          // Set initial selected account
          if (response.data.length > 0 && !selectedOrganization) {
            setSelectedOrganization(response.data[0].id);
          }
        }
      } catch (error) {
        console.error('Error fetching billing accounts:', error);
      } finally {
        setLoadingOrganizations(false);
      }
    };

    fetchBillingAccounts();
  }, []); // Run once on component mount

  // Prepare data for pie chart
  const pieChartData = {
    series: data.map((item) => item.totalHoursUtilized), // Removed /3600 conversion
    labels: data.map((item) => item.accountName),
  };

  // Configure columns for the table
  const columns = [
    {
      id: 'accountName',
      label: 'Account',
      align: 'left' as const,
    },
    {
      id: 'totalHoursAllocated',
      label: 'Hours Allocated',
      align: 'right' as const,
      format: (value: number) => value.toFixed(2),
    },
    {
      id: 'totalHoursUtilized',
      label: 'Hours Utilized',
      align: 'right' as const,
      format: (value: number) => value.toFixed(2),
    },
  ];

  const totals: Totals = {
    totalHoursUtilized: data.reduce(
      (sum, item) => sum + item.totalHoursUtilized,
      0
    ),
    totalHoursAllocated: data.reduce(
      (sum, item) => sum + item.totalHoursAllocated,
      0
    ),
  };

  return (
    <Box
      sx={{
        backgroundColor: 'background.default',
        minHeight: '100%',
        px: 2,
        py: 4,
      }}>
      <Typography
        color="textPrimary"
        variant="h3"
        alignSelf="flex-start"
        flex={1}>
        Dashboard V2
      </Typography>

      <Grid container spacing={2} sx={{ mt: 3, minHeight: '400px' }}>
        <Grid item xs={4}>
          <Box
            sx={{
              height: '100%',
              backgroundColor: 'white',
              borderRadius: 1,
            }}>
            <Box
              sx={{
                height: '10%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 1,
                paddingTop: 2,
                marginBottom: 1,
              }}>
              <Select
                value={selectedSprint}
                onChange={(e) => setSelectedSprint(Number(e.target.value))}
                size="small"
                sx={{
                  minWidth: 150,
                  height: 32,
                  '& .MuiSelect-select': {
                    padding: '4px 14px',
                    fontSize: '0.875rem',
                  },
                }}>
                <MenuItem value={0}>Current Sprint</MenuItem>
                <MenuItem value={1}>Second Last Sprint</MenuItem>
                <MenuItem value={2}>Last Sprint</MenuItem>
              </Select>
              <IconButton onClick={() => setShowTable(!showTable)} size="small">
                {showTable ? <PieChartIcon /> : <TableChartIcon />}
              </IconButton>
            </Box>

            {showTable ? (
              <DashboardSummaryTable
                data={data}
                columns={columns}
                totals={totals}
              />
            ) : (
              <PieChart data={pieChartData} />
            )}
          </Box>
        </Grid>
        {/* <Grid item xs={4}>
          <Box
            sx={{
              height: '100%',
              backgroundColor: 'white',
              borderRadius: 1,
              p: 2,
            }}>
            <SprintTrendChart />
          </Box>
        </Grid> */}
        <Grid item xs={8}>
          <Box
            sx={{
              height: '100%',
              backgroundColor: 'white',
              borderRadius: 1,
              p: 1,
            }}>
            <FormControl
              size="small"
              sx={{
                minWidth: 150,
                '& .MuiInputBase-root': {
                  height: 32,
                  fontSize: '0.75rem',
                },
                '& .MuiSelect-select': {
                  py: 0.5,
                  px: 1,
                },
                mb: 1.5,
              }}>
              <Select
                value={selectedOrganization}
                onChange={(e) => setSelectedOrganization(e.target.value)}
                disabled={loadingOrganizations}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 200,
                    },
                  },
                }}>
                {organizations.map((account) => (
                  <MenuItem
                    key={account.id}
                    value={account.id}
                    sx={{
                      fontSize: '0.75rem',
                      py: 0.5,
                      minHeight: 'auto',
                    }}>
                    {account.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <MixedChart
              selectedOrganization={selectedOrganization}
              organizationData={organizations.find(
                (org) => org.id === selectedOrganization
              )}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardV2;
